import type {UnionObjectTypesMap} from './types';

/**
 * This is required to correctly handle fragments on union types, particularly where union members have fields with
 * the same name but incompatible types.
 *
 * It returns `true` to execute a fragment on type `typeCondition` against `rootValue`, a data object. Any executed
 * fragments are merged. By default all fragments are executed against all data; see:
 * https://github.com/apollographql/apollo-client/tree/64cda6af5ebd1b917d8838790e043cc70dab4ed2/packages/graphql-anywhere#options
 *
 * Here's a relevant issue that involves a similar solution to ours:
 * - https://github.com/apollographql/apollo-client/issues/3802
 * I think this might no longer be needed with Apollo Client 3; here is a relevant issue:
 * - https://github.com/apollographql/apollo-client/issues/6632
 * - possibleTypes: https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
 */
export default function fragmentMatcher(
  unionObjectTypesMap: UnionObjectTypesMap,

  rootValue: any,
  typeCondition: string,
) {
  const unionTypenames = unionObjectTypesMap[rootValue.__typename];
  if (!unionTypenames) {
    return true; // fail open
  }
  const typenamesForObject = unionTypenames[rootValue.object];
  if (!typenamesForObject) {
    return true; // fail open
  }

  if (Array.isArray(typenamesForObject)) {
    if (typenamesForObject.length === 2) {
      const isDeleted = rootValue.deleted === true;
      const expectedType = isDeleted
        ? typenamesForObject[1]
        : typenamesForObject[0];
      return typeCondition === expectedType;
    }

    return typenamesForObject.includes(typeCondition);
  }

  return false;
}
